import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowsClockwiseIcon,
  BooksIcon,
  CheckIcon,
  PencilSimpleIcon,
  PlusIcon,
  TrashIcon,
  XIcon,
} from '@phosphor-icons/react';
import {
  approveKbFact,
  createKbFact,
  deleteKbFact,
  fetchKbFacts,
  patchKbFact,
  rebuildKbFacts,
  rejectKbFact,
  type KbFact,
  type KbFactStatus,
} from '@/features/kb-facts/api';
import { fetchProperties, type PropertyRow } from '@/features/properties/api';
import { Button, Card, CardContent, Input, Label, PageHeader, StatusChip, cn, fmtRelativeTime, type Tone } from '@smena/ui';

const STATUS_TONE: Record<KbFactStatus, Tone> = {
  draft: 'amber',
  approved: 'green',
  rejected: 'red',
};

export function KbPage() {
  const [filterProperty, setFilterProperty] = useState<string>(''); // '' - все, 'null' - только аккаунт, uuid - объект
  const [filterStatus, setFilterStatus] = useState<KbFactStatus | ''>('');
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);

  const qc = useQueryClient();
  const propsQ = useQuery({ queryKey: ['properties'], queryFn: fetchProperties });
  const factsQ = useQuery({
    queryKey: ['kb-facts', filterProperty, filterStatus, q],
    queryFn: () =>
      fetchKbFacts({
        ...(filterProperty ? { propertyId: filterProperty as 'null' | string } : {}),
        ...(filterStatus ? { status: filterStatus } : {}),
        ...(q ? { q } : {}),
      }),
    refetchInterval: 15_000,
  });

  const rebuildMut = useMutation({
    mutationFn: () =>
      rebuildKbFacts({
        ...(filterProperty && filterProperty !== 'null' ? { propertyId: filterProperty } : {}),
      }),
    onSuccess: (r) => {
      toast.success(
        `Обработано ${r.scanned} документов: ${r.drafted} новых черновиков, ${r.updated} обновлено, ${r.deduped} пропущено (дубли)`,
      );
      void qc.invalidateQueries({ queryKey: ['kb-facts'] });
    },
    onError: (e: Error) => {
      // 403 - флаг KB_FACT_MINING не установлен на сервере (безопасный канат).
      if (String(e.message).includes('403')) {
        toast.error('Автосбор фактов выключен на сервере - обратитесь к нам, включим.');
      } else {
        toast.error(`Ошибка: ${e.message}`);
      }
    },
  });

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      <div className="flex items-baseline justify-between">
        <div>
          <PageHeader
            icon={<BooksIcon weight="bold" />}
            title="База знаний"
            description="Что Smena знает о ваших квартирах: коды, Wi-Fi, правила, что рядом. Отсюда она берёт ответы гостю."
          />
        </div>
        <div className="flex items-center gap-2 text-xs">
          {factsQ.data ? (
            <>
              <StatusChip tone="green">{factsQ.data.counts.approved} approved</StatusChip>
              <StatusChip tone="amber">{factsQ.data.counts.draft} draft</StatusChip>
              <StatusChip tone="muted">{factsQ.data.counts.rejected} rejected</StatusChip>
            </>
          ) : null}
        </div>
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 p-4">
          <div className="flex flex-col gap-1">
            <Label>Объект</Label>
            <select
              value={filterProperty}
              onChange={(e) => setFilterProperty(e.target.value)}
              className="rounded border border-line-control bg-input px-2 py-1.5 text-sm"
            >
              <option value="">Все</option>
              <option value="null">Общие (без объекта)</option>
              {propsQ.data?.map((p: PropertyRow) => (
                <option key={p.id} value={p.id}>
                  {p.title}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <Label>Статус</Label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as KbFactStatus | '')}
              className="rounded border border-line-control bg-input px-2 py-1.5 text-sm"
            >
              <option value="">Все</option>
              <option value="approved">Approved</option>
              <option value="draft">Draft</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div className="flex flex-1 flex-col gap-1">
            <Label>Поиск</Label>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ключ или содержимое" />
          </div>
          <Button
            variant="secondary"
            onClick={() => rebuildMut.mutate()}
            disabled={rebuildMut.isPending}
            title="Собрать факты из ваших объявлений и переписок"
          >
            <ArrowsClockwiseIcon weight="bold" className={rebuildMut.isPending ? 'animate-spin' : undefined} />
            {rebuildMut.isPending ? 'Собираем…' : 'Собрать факты заново'}
          </Button>
          <Button onClick={() => setCreating(true)}>
            <PlusIcon weight="bold" /> Добавить
          </Button>
        </CardContent>
      </Card>

      {creating ? (
        <CreateFactCard
          properties={propsQ.data ?? []}
          initialPropertyId={filterProperty === '' || filterProperty === 'null' ? null : filterProperty}
          onClose={() => setCreating(false)}
        />
      ) : null}

      {factsQ.isLoading ? (
        <Card>
          <CardContent className="p-6 text-center text-sm text-muted">Загрузка…</CardContent>
        </Card>
      ) : !factsQ.data || factsQ.data.items.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center text-sm text-muted">
            Ничего не найдено. Импортируйте объявление на «Объекты» - парсер создаст первые
            structural-факты.
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-2">
          {factsQ.data.items.map((f) => (
            <FactRow key={f.id} fact={f} properties={propsQ.data ?? []} />
          ))}
        </div>
      )}
    </div>
  );
}

function CreateFactCard({
  properties,
  initialPropertyId,
  onClose,
}: {
  properties: PropertyRow[];
  initialPropertyId: string | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [propertyId, setPropertyId] = useState<string>(initialPropertyId ?? '');
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');

  const save = useMutation({
    mutationFn: () =>
      createKbFact({
        propertyId: propertyId || null,
        key: key.trim(),
        value: value.trim(),
      }),
    onSuccess: () => {
      setKey('');
      setValue('');
      onClose();
      void qc.invalidateQueries({ queryKey: ['kb-facts'] });
      toast.success('Факт добавлен');
    },
    onError: (e: Error) => toast.error(`Ошибка: ${e.message}`),
  });

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-4">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold">Новый факт</div>
          <button onClick={onClose} className="text-muted hover:text-text" aria-label="Закрыть">
            <XIcon className="size-4" />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1">
            <Label>Объект</Label>
            <select
              value={propertyId}
              onChange={(e) => setPropertyId(e.target.value)}
              className="h-10 rounded border border-line-control bg-input px-2 text-sm"
            >
              <option value="">Общий факт (без объекта)</option>
              {properties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <Label>Ключ</Label>
            <Input value={key} onChange={(e) => setKey(e.target.value)} placeholder="Например: кофейня рядом" />
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <Label>Что Smena должна знать</Label>
          <textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            rows={4}
            className="w-full resize-y rounded-md border border-line-control bg-input px-3 py-2 text-sm"
            placeholder="Данные / инструкция агенту / заготовка текста. Пример: «Кофейня Двор - Гончарная 15, 8:00-22:00, скидка 10% по слову Smena»."
          />
        </div>
        <div className="flex gap-2">
          <Button onClick={() => save.mutate()} disabled={!key.trim() || !value.trim() || save.isPending}>
            Сохранить
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function FactRow({ fact, properties }: { fact: KbFact; properties: PropertyRow[] }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(fact.value);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['kb-facts'] });
  const propName = fact.propertyId
    ? (properties.find((p) => p.id === fact.propertyId)?.title ?? '(объект)')
    : 'Общий';

  const save = useMutation({
    mutationFn: () => patchKbFact(fact.id, { value: text.trim() }),
    onSuccess: () => {
      setEditing(false);
      void invalidate();
      toast.success('Сохранено');
    },
  });
  const approve = useMutation({
    mutationFn: () => approveKbFact(fact.id),
    onSuccess: () => {
      void invalidate();
      toast.success('Approved');
    },
  });
  const reject = useMutation({
    mutationFn: () => rejectKbFact(fact.id),
    onSuccess: () => {
      void invalidate();
      toast.success('Rejected');
    },
  });
  const remove = useMutation({
    mutationFn: () => deleteKbFact(fact.id),
    onSuccess: () => {
      void invalidate();
      toast.success('Удалено');
    },
  });

  return (
    <Card
      className={cn(
        'border-l-4',
        fact.status === 'approved' ? 'border-l-green' : fact.status === 'draft' ? 'border-l-amber' : 'border-l-line',
      )}
    >
      <CardContent className="pt-4">
        <div className="mb-2 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold">{fact.key}</span>
            <StatusChip tone={STATUS_TONE[fact.status]}>{fact.status}</StatusChip>
            <span className="text-[11px] text-muted">
              {propName} · {fact.source} · {fmtRelativeTime(fact.updatedAt)}
            </span>
          </div>
        </div>
        {editing ? (
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={5}
            className="w-full resize-y rounded-md border border-line-control bg-input px-3 py-2 text-sm"
          />
        ) : (
          <div className="whitespace-pre-wrap rounded-md bg-panel2/60 p-3 text-sm">{fact.value}</div>
        )}
        <div className="mt-3 flex flex-wrap gap-2">
          {editing ? (
            <>
              <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>
                <CheckIcon weight="bold" /> Сохранить
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                Отмена
              </Button>
            </>
          ) : (
            <>
              <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>
                <PencilSimpleIcon weight="bold" /> Править
              </Button>
              {fact.status !== 'approved' ? (
                <Button size="sm" onClick={() => approve.mutate()}>
                  <CheckIcon weight="bold" /> Approve
                </Button>
              ) : null}
              {fact.status !== 'rejected' ? (
                <Button size="sm" variant="ghost" onClick={() => reject.mutate()}>
                  <XIcon weight="bold" /> Reject
                </Button>
              ) : null}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  if (confirm(`Удалить факт «${fact.key}»?`)) remove.mutate();
                }}
                className="ml-auto text-destructive"
              >
                <TrashIcon weight="bold" />
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
